// Minecart render (M16.2): a pooled open-top box per cart, positioned at the rail
// centreline the sim derives and yawed to its heading. Client-only (ENG-1); the sim
// owns the rail physics. Proper cart model + contents (chest/furnace) are deferred.
import * as THREE from 'three';
import type { Minecart } from '../../sim/entities/minecart';

const CART = 0x6b6b6b;
const YAW: Record<string, number> = { north: 0, south: Math.PI, east: -Math.PI / 2, west: Math.PI / 2 };

export class MinecartRenderer {
  private readonly group = new THREE.Group();
  private readonly meshes = new Map<number, THREE.Mesh>();
  private readonly geo = new THREE.BoxGeometry(0.9, 0.5, 0.9);

  constructor(scene: THREE.Scene) {
    scene.add(this.group);
  }

  update(carts: readonly Minecart[]): void {
    const seen = new Set<number>();
    for (const c of carts) {
      seen.add(c.id);
      let m = this.meshes.get(c.id);
      if (!m) {
        m = new THREE.Mesh(this.geo, new THREE.MeshLambertMaterial({ color: CART }));
        this.meshes.set(c.id, m);
        this.group.add(m);
      }
      m.position.set(c.x, c.y + 0.25, c.z);
      m.rotation.y = YAW[c.dir] ?? 0;
    }
    for (const [id, m] of this.meshes) {
      if (seen.has(id)) continue;
      this.group.remove(m);
      (m.material as THREE.Material).dispose();
      this.meshes.delete(id);
    }
  }

  clear(): void {
    for (const m of this.meshes.values()) {
      this.group.remove(m);
      (m.material as THREE.Material).dispose();
    }
    this.meshes.clear();
  }
}
